/**
 * Analytics Tracking Hooks
 * Reusable hooks for tracking user interactions across the application
 *
 * Created: 2025-11-28
 * Updated: 2025-12-12 - Added ReactGA integration for proper GA4 page views
 */

import { useEffect, useRef, useCallback } from 'react';
import ReactGA from 'react-ga4';
import {
  trackEvent,
  trackNavigation,
  trackForm,
  trackVideo,
  trackError
} from '../services/analytics';

// ============================================
// useButtonTracking - Auto-track button clicks
// ============================================

interface ButtonTrackingOptions {
  buttonId: string;
  buttonText: string;
  location: string;
}

export const useButtonTracking = (options: ButtonTrackingOptions) => {
  const handleClick = useCallback(
    (e: React.MouseEvent, additionalCallback?: () => void) => {
      trackNavigation.buttonClick(options.buttonId, options.buttonText, options.location);
      if (additionalCallback) {
        additionalCallback();
      }
    },
    [options.buttonId, options.buttonText, options.location]
  );

  return handleClick;
};

// ============================================
// useFormTracking - Track form interactions
// ============================================

interface FormTrackingOptions {
  formId: string;
  formName: string;
}

export const useFormTracking = (options: FormTrackingOptions) => {
  const formStartedRef = useRef(false);
  const startTimeRef = useRef<number>(Date.now());
  const lastFieldRef = useRef<string>('');

  const trackFormStart = useCallback(() => {
    if (!formStartedRef.current) {
      trackForm.formStart(options.formId, options.formName);
      formStartedRef.current = true;
      startTimeRef.current = Date.now();
    }
  }, [options.formId, options.formName]);

  const trackFieldFocus = useCallback(
    (fieldName: string) => {
      trackFormStart();
      trackForm.fieldFocus(options.formId, fieldName);
      lastFieldRef.current = fieldName;
    },
    [options.formId, trackFormStart]
  );

  const trackFieldComplete = useCallback(
    (fieldName: string) => {
      trackForm.fieldComplete(options.formId, fieldName);
    },
    [options.formId]
  );

  const trackSubmit = useCallback(
    (success: boolean) => {
      trackForm.formSubmit(options.formId, options.formName, success);
    },
    [options.formId, options.formName]
  );

  const trackFormError = useCallback(
    (error: string, field?: string) => {
      trackForm.formError(options.formId, options.formName, error, field);
    },
    [options.formId, options.formName]
  );

  // Track form abandonment on unmount
  useEffect(() => {
    return () => {
      if (formStartedRef.current) {
        const timeSpent = Date.now() - startTimeRef.current;
        // Only track abandon if form wasn't submitted (timeSpent > 2 seconds suggests interaction)
        if (timeSpent > 2000 && lastFieldRef.current) {
          trackForm.formAbandon(
            options.formId,
            options.formName,
            lastFieldRef.current,
            timeSpent
          );
        }
      }
    };
  }, [options.formId, options.formName]);

  return {
    trackFormStart,
    trackFieldFocus,
    trackFieldComplete,
    trackSubmit,
    trackFormError
  };
};

// ============================================
// useVideoTracking - Track video events
// ============================================

interface VideoTrackingOptions {
  videoId: string;
  videoUrl: string;
  location?: string;
}

export const useVideoTracking = (options: VideoTrackingOptions) => {
  const progressMilestonesRef = useRef({
    25: false,
    50: false,
    75: false,
    100: false
  });
  const watchTimeRef = useRef(0);
  const lastTimeRef = useRef(0);

  const trackPlay = useCallback(() => {
    trackVideo.play(options.videoId, options.videoUrl, options.location || 'unknown');
  }, [options.videoId, options.videoUrl, options.location]);

  const trackPause = useCallback(
    (currentTime: number, duration: number) => {
      trackVideo.pause(options.videoId, currentTime, duration);
    },
    [options.videoId]
  );

  const trackSeek = useCallback(
    (fromTime: number, toTime: number) => {
      trackVideo.seek(options.videoId, fromTime, toTime);
    },
    [options.videoId]
  );

  const trackProgress = useCallback(
    (currentTime: number, duration: number) => {
      // Update watch time
      watchTimeRef.current += currentTime - lastTimeRef.current;
      lastTimeRef.current = currentTime;

      const percent = Math.floor((currentTime / duration) * 100);

      // Track milestones
      const milestones = [25, 50, 75, 100] as const;
      for (const milestone of milestones) {
        if (
          percent >= milestone &&
          !progressMilestonesRef.current[milestone]
        ) {
          trackVideo.progress(options.videoId, milestone, currentTime, duration);
          progressMilestonesRef.current[milestone] = true;
        }
      }
    },
    [options.videoId]
  );

  const trackComplete = useCallback(
    (duration: number) => {
      trackVideo.complete(options.videoId, duration, watchTimeRef.current);
    },
    [options.videoId]
  );

  const trackVideoError = useCallback(
    (error: string) => {
      trackVideo.error(options.videoId, options.videoUrl, error);
    },
    [options.videoId, options.videoUrl]
  );

  const trackMuteToggle = useCallback(
    (isMuted: boolean) => {
      trackVideo.muteToggle(options.videoId, isMuted);
    },
    [options.videoId]
  );

  const trackFullscreenToggle = useCallback(
    (isFullscreen: boolean) => {
      trackVideo.fullscreenToggle(options.videoId, isFullscreen);
    },
    [options.videoId]
  );

  const resetProgress = useCallback(() => {
    progressMilestonesRef.current = { 25: false, 50: false, 75: false, 100: false };
    watchTimeRef.current = 0;
    lastTimeRef.current = 0;
  }, []);

  return {
    trackPlay,
    trackPause,
    trackSeek,
    trackProgress,
    trackComplete,
    trackVideoError,
    trackMuteToggle,
    trackFullscreenToggle,
    resetProgress
  };
};

// ============================================
// useScrollTracking - Track scroll depth
// ============================================

export const useScrollTracking = (pageName: string) => {
  const trackedRef = useRef({
    depth25: false,
    depth50: false,
    depth75: false,
    depth90: false
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      const scrollPercent = (window.scrollY / scrollHeight) * 100;

      if (scrollPercent > 25 && !trackedRef.current.depth25) {
        trackEvent('Scroll_Depth', { depth: 25, page: pageName });
        trackedRef.current.depth25 = true;
      }
      if (scrollPercent > 50 && !trackedRef.current.depth50) {
        trackEvent('Scroll_Depth', { depth: 50, page: pageName });
        trackedRef.current.depth50 = true;
      }
      if (scrollPercent > 75 && !trackedRef.current.depth75) {
        trackEvent('Scroll_Depth', { depth: 75, page: pageName });
        trackedRef.current.depth75 = true;
      }
      if (scrollPercent > 90 && !trackedRef.current.depth90) {
        trackEvent('Scroll_Depth', { depth: 90, page: pageName });
        trackedRef.current.depth90 = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageName]);
};

// ============================================
// usePageTracking - Track page views and time on page
// ============================================

export const usePageTracking = (pageName: string, additionalProps?: Record<string, any>) => {
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Send to GA4 with proper page title
    try {
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname,
        title: pageName
      });
    } catch (e) {
      // Silently fail
    }

    // Track custom page view event for detailed analytics
    trackEvent('Page_View', {
      pageName,
      url: window.location.pathname,
      referrer: document.referrer,
      ...additionalProps,
      timestamp: Date.now()
    });

    startTimeRef.current = Date.now();

    // Track time on page when leaving
    return () => {
      const timeOnPage = Date.now() - startTimeRef.current;
      trackEvent('Page_Exit', {
        pageName,
        timeOnPage,
        url: window.location.pathname
      });
    };
  }, [pageName]);
};

// ============================================
// useErrorTracking - Track component errors
// ============================================

export const useErrorTracking = (context: string) => {
  const trackComponentError = useCallback(
    (error: Error) => {
      trackError.generalError(error.message, context, error.stack);
    },
    [context]
  );

  const trackApiError = useCallback(
    (endpoint: string, status: number, error: string, method?: string) => {
      trackError.apiError(endpoint, status, error, method);
    },
    []
  );

  return {
    trackComponentError,
    trackApiError
  };
};

// ============================================
// useEnrollmentTracking - Track enrollment flow
// ============================================

import { trackEnrollment } from '../services/analytics';

interface EnrollmentTrackingOptions {
  courseId: string;
  bundleId?: string | null;
  source?: string;
}

export const useEnrollmentTracking = (options: EnrollmentTrackingOptions) => {
  const stepRef = useRef<string>('start');
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Track enrollment start
    trackEnrollment.start(
      options.courseId,
      options.bundleId || null,
      options.source || 'direct'
    );
    startTimeRef.current = Date.now();

    // Track abandonment on unmount
    return () => {
      const timeOnPage = Date.now() - startTimeRef.current;
      if (stepRef.current !== 'complete') {
        trackEnrollment.abandon(stepRef.current, timeOnPage, options.courseId);
      }
    };
  }, [options.courseId, options.bundleId, options.source]);

  const trackStep = useCallback(
    (step: string, stepNumber: number) => {
      stepRef.current = step;
      trackEnrollment.stepComplete(step, stepNumber, options.courseId);
    },
    [options.courseId]
  );

  const trackPaymentStart = useCallback(
    (amount: number, productType: string) => {
      stepRef.current = 'payment';
      trackEnrollment.paymentStart(amount, productType, options.courseId);
    },
    [options.courseId]
  );

  const trackPaymentSuccess = useCallback(
    (transactionId: string, amount: number, productType: string) => {
      stepRef.current = 'complete';
      trackEnrollment.paymentSuccess(transactionId, amount, productType, options.courseId);
    },
    [options.courseId]
  );

  const trackPaymentFailure = useCallback(
    (error: string, errorCode: string, amount: number) => {
      trackEnrollment.paymentFailure(error, errorCode, amount, options.courseId);
    },
    [options.courseId]
  );

  const trackComplete = useCallback(
    (userId: string, transactionId: string) => {
      stepRef.current = 'complete';
      trackEnrollment.complete(options.courseId, userId, transactionId);
    },
    [options.courseId]
  );

  return {
    trackStep,
    trackPaymentStart,
    trackPaymentSuccess,
    trackPaymentFailure,
    trackComplete
  };
};

// ============================================
// useClassroomTracking - Track classroom activity
// ============================================

import { trackClassroom } from '../services/analytics';

interface ClassroomTrackingOptions {
  courseId: string;
  courseName: string;
}

export const useClassroomTracking = (options: ClassroomTrackingOptions) => {
  useEffect(() => {
    trackClassroom.enter(options.courseId, options.courseName);
  }, [options.courseId, options.courseName]);

  const trackModuleSelect = useCallback(
    (moduleIndex: number, moduleName: string) => {
      trackClassroom.moduleSelect(moduleIndex, moduleName, options.courseId);
    },
    [options.courseId]
  );

  const trackLessonSelect = useCallback(
    (lessonIndex: number, lessonName: string, moduleId: string) => {
      trackClassroom.lessonSelect(lessonIndex, lessonName, moduleId, options.courseId);
    },
    [options.courseId]
  );

  const trackTopicSelect = useCallback(
    (topicIndex: number, topicName: string, lessonId: string) => {
      trackClassroom.topicSelect(topicIndex, topicName, lessonId, options.courseId);
    },
    [options.courseId]
  );

  const trackVideoPlay = useCallback(
    (topicId: string, videoUrl: string) => {
      trackClassroom.videoPlay(topicId, videoUrl, options.courseId);
    },
    [options.courseId]
  );

  const trackVideoProgress = useCallback(
    (topicId: string, progress: number) => {
      trackClassroom.videoProgress(topicId, progress, options.courseId);
    },
    [options.courseId]
  );

  const trackVideoComplete = useCallback(
    (topicId: string, watchTime: number) => {
      trackClassroom.videoComplete(topicId, watchTime, options.courseId);
    },
    [options.courseId]
  );

  const trackLessonComplete = useCallback(
    (lessonId: string, lessonName: string) => {
      trackClassroom.lessonComplete(lessonId, lessonName, options.courseId);
    },
    [options.courseId]
  );

  const trackNavPrevious = useCallback(
    (fromTopic: string, toTopic: string) => {
      trackClassroom.navigationPrevious(fromTopic, toTopic, options.courseId);
    },
    [options.courseId]
  );

  const trackNavNext = useCallback(
    (fromTopic: string, toTopic: string) => {
      trackClassroom.navigationNext(fromTopic, toTopic, options.courseId);
    },
    [options.courseId]
  );

  const trackSidebarToggle = useCallback(
    (collapsed: boolean) => {
      trackClassroom.sidebarToggle(collapsed, options.courseId);
    },
    [options.courseId]
  );

  return {
    trackModuleSelect,
    trackLessonSelect,
    trackTopicSelect,
    trackVideoPlay,
    trackVideoProgress,
    trackVideoComplete,
    trackLessonComplete,
    trackNavPrevious,
    trackNavNext,
    trackSidebarToggle
  };
};
